import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { PrimaryButton } from '../components/PrimaryButton';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { YourGiftCardPreview } from '../components/YourGiftCardPreview';
import { GiftCardStepper } from '../components/GiftCardStepper';
import { ThemedDatePicker } from '../components/ThemedDatePicker';
import { getGiftCardConfig } from '../../lib/gift-card-api';
import {
  getGiftCardDraft,
  patchGiftCardDraft,
} from '../../lib/gift-card-draft';

const POPULAR_AMOUNT = 50;

// Today as YYYY-MM-DD — used as the date picker's lower bound so a gift
// can't be scheduled into the past.
const todayYmd = (): string => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

export default function GiftCardPage() {
  const navigate = useNavigate();
  // Amount presets + bounds are admin-configured (loaded from the API).
  const [giftCardAmounts, setGiftCardAmounts] = useState<number[]>([]);
  const [minAmount, setMinAmount] = useState<number>(20);
  const [maxAmount, setMaxAmount] = useState<number>(1000);
  // Seed from the persistent draft so a back-navigation from the
  // recipient step doesn't blank the amount / send-date.
  const draft = getGiftCardDraft();
  const [selectedAmount, setSelectedAmount] = useState<number>(
    draft?.amount ?? 0,
  );
  const [customAmount, setCustomAmount] = useState<string>(
    draft?.customAmount ?? '',
  );
  const [customAmountError, setCustomAmountError] = useState<string | null>(null);
  // Empty by default — no date is preselected; leaving it blank sends
  // the card immediately on purchase.
  const [sendDate, setSendDate] = useState<string>(draft?.sendDate ?? '');

  const today = useMemo(() => todayYmd(), []);

  useEffect(() => {
    let cancelled = false;
    getGiftCardConfig()
      .then((res) => {
        if (cancelled) return;
        const cfg = res.info;
        setGiftCardAmounts(cfg.preset_amounts ?? []);
        setMinAmount(cfg.min_amount);
        setMaxAmount(cfg.max_amount);
        // Default selection: keep any prior draft value, otherwise the
        // "Most Popular" tile if it's offered, otherwise the lowest
        // preset / the configured minimum.
        const presets = cfg.preset_amounts ?? [];
        const fallback = presets[0] ?? cfg.min_amount;
        setSelectedAmount((cur) =>
          cur > 0
            ? cur
            : presets.includes(POPULAR_AMOUNT)
              ? POPULAR_AMOUNT
              : fallback,
        );
      })
      .catch(() => {
        // Non-fatal: leave the grid empty and let the custom-amount
        // field carry the flow.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAmountClick = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
    setCustomAmountError(null);
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setCustomAmount(value);
      setSelectedAmount(Number(value) || 0);

      if (value) {
        const amount = Number(value);
        if (amount < minAmount) {
          setCustomAmountError(`Minimum amount is £${minAmount}.`);
        } else if (amount > maxAmount) {
          setCustomAmountError(`Maximum amount is £${maxAmount}.`);
        } else {
          setCustomAmountError(null);
        }
      } else {
        setCustomAmountError(null);
      }
    }
  };

  const handleProceed = () => {
    if (customAmountError) return;
    if (customAmount && !selectedAmount) return;

    // Persist the customer's amount + send-date into the draft so the
    // recipient / delivery / summary steps all see them, and so a Back
    // press to this page restores the same values.
    patchGiftCardDraft({
      amount: selectedAmount,
      customAmount,
      sendDate,
    });
    navigate('/gift-card/recipient');
  };

  const currentStep = 0;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <div className="flex-1 max-w-[1140px] mx-auto px-4 md:px-8 py-8 md:py-12 pb-32 md:pb-12 w-full">
        <GiftCardStepper currentStep={currentStep} />

        <div className="flex flex-col lg:flex-row gap-10">
          {/* Left column */}
          <div className="flex-1 max-w-[740px]">
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] md:text-[28px] leading-[36px] tracking-[-0.0857px] mb-6">
              Gift Card
            </h1>

            {/* Amount grid */}
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 md:gap-4 mb-6">
              {giftCardAmounts.map((amount) => {
                const isSelected = !customAmount && selectedAmount === amount;
                const isPopular = amount === POPULAR_AMOUNT;
                return (
                  <button
                    key={amount}
                    onClick={() => handleAmountClick(amount)}
                    className={`relative h-[88px] rounded-[8px] border bg-white flex flex-col items-center justify-center gap-1 transition-all cursor-pointer ${
                      isSelected
                        ? 'border-[#8e2a2f]'
                        : 'border-[#eee] hover:border-[#8e2a2f]/40'
                    }`}
                  >
                    {isPopular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#8e2a2f] text-white font-['DM_Sans',sans-serif] font-bold text-[11px] tracking-[-0.1px] px-[8px] py-[3px] rounded-[30px] whitespace-nowrap">
                        Most Popular
                      </span>
                    )}
                    {isSelected && (
                      <span className="material-icons absolute top-1.5 right-1.5 text-[16px] text-[#8e2a2f]">
                        check_circle
                      </span>
                    )}
                    <span className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[20px] md:text-[24px] leading-[28px] tracking-[-0.0857px]">
                      £{amount}
                    </span>
                    <span className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[12px] md:text-[14px] tracking-[-0.1px]">
                      Gift Card
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Custom amount */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
              <label
                htmlFor="custom-amount"
                className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] tracking-[-0.1px] whitespace-nowrap"
              >
                Or a Custom Amount :
              </label>
              <div className="relative h-[44px] w-full sm:w-[262px]">
                <span className="absolute left-[20px] top-1/2 -translate-y-1/2 font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] leading-none">
                  £
                </span>
                <input
                  id="custom-amount"
                  type="text"
                  inputMode="numeric"
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  placeholder="e.g. 25"
                  className="w-full h-full pl-[40px] pr-4 bg-white border border-[#ede8e9] rounded-[8px] outline-none font-['DM_Sans',sans-serif] font-bold text-[14px] text-[#3a3335] placeholder:text-[#908e8e] focus:border-[#8e2a2f] transition-colors"
                />
              </div>
            </div>
            <p className="font-['Inter',sans-serif] text-[#908e8e] text-[16px] leading-[24px] mb-2">
              Minimum £{minAmount} – maximum £{maxAmount}
            </p>
            {customAmountError && (
              <p className="font-['Inter',sans-serif] text-[#991b1b] text-[14px] mb-6">
                {customAmountError}
              </p>
            )}

            {/* Date picker — themed, no past dates, blank by default.
                The popover's Clear button resets it to "send now". */}
            <div className="w-full max-w-[420px] mb-2">
              <ThemedDatePicker
                label="When should we send it?"
                value={sendDate}
                onChange={setSendDate}
                min={today}
                placeholder="Send immediately"
              />
            </div>
            <p className="font-['Inter',sans-serif] text-[#3a3335] text-[14px] mb-10">
              Leave blank to send immediately, when your purchase is complete
            </p>

            {/* Continue button */}
            <div className="hidden md:flex justify-center">
              <PrimaryButton
                onClick={handleProceed}
                className="w-full max-w-[420px] h-[54px]"
                disabled={customAmountError !== null || !selectedAmount}
              >
                Continue
              </PrimaryButton>
            </div>

            {/* Mobile sticky button */}
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-[#eee] md:hidden z-50">
              <PrimaryButton
                onClick={handleProceed}
                className="w-full h-[54px]"
                disabled={customAmountError !== null || !selectedAmount}
              >
                Continue
              </PrimaryButton>
            </div>
          </div>

          {/* Right sidebar */}
          <div className="w-full lg:w-[361px] shrink-0">
            <YourGiftCardPreview amount={selectedAmount} />

            {/* Price breakdown */}
            <div className="mt-6 border border-[#ede8e9] rounded-[10px] p-5 bg-white">
              <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] tracking-[-0.1px] mb-4">
                Price breakdown
              </p>
              <div className="flex justify-between items-center mb-3">
                <span className="font-['Inter',sans-serif] text-[#3a3335] text-[16px] leading-[24px]">
                  Gift Card Value
                </span>
                <span className="font-['Inter',sans-serif] text-[#8e2a2f] text-[16px] leading-[24px]">
                  £{selectedAmount.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center mb-4">
                <span className="font-['Inter',sans-serif] text-[#908e8e] text-[16px] leading-[24px]">
                  Delivery
                </span>
                <span className="font-['Inter',sans-serif] text-[#3a3335] text-[16px] leading-[24px]">
                  Free . Instant Email
                </span>
              </div>
              <div className="h-px bg-[#ede8e9] w-full mb-4" />
              <div className="flex justify-between items-center">
                <span className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[16px] leading-[24px]">
                  Total
                </span>
                <span className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] leading-[27px]">
                  £{selectedAmount.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Feature bullets */}
            <div className="mt-5 space-y-3">
              <div className="flex items-start gap-2.5">
                <span className="material-icons text-[#8e2a2f] text-[20px] leading-[21px]">
                  redeem
                </span>
                <p className="font-['DM_Sans',sans-serif] font-medium text-[#3a3335] text-[14px] leading-[21px] tracking-[-0.1px]">
                  Redeemable against any Cherry Picked service
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
